// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useMemo } from "react";
import "./SupplierTable.scss";

import { AuthContext } from "../../context/AuthContext";
import SupplierOverview from "./SupplierOverview";
import { getSortedSupplierIds, exportToExcel } from "./SupplierTableDataLoader";
import { useTranslation } from "react-i18next";
import Tooltip from "@mui/material/Tooltip";
import LoadingIcon from "../../components/LoadingIcon/LoadingIcon";

export default function SupplierTable({
  suppliers,
  supplierDataFiltered,
  tableEntries,
  supplierData = {},
  supplierDataGrouped = {},
  tableEntriesGrouped = {},
  searchQuery = "",
  activeFilters = {},
  defaultSortColumn = "name",
  defaultSortDirection = "asc",
  onSortChange = null,
  defaultPageSize = 5,
  showSearchInfo = true,
  isLoading = false,
  sortedColumns,
}) {
  const { logEventCustom, currentUser } = React.useContext(AuthContext);
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [chartViewMode, setChartViewMode] = useState("qty");
  const { t } = useTranslation();
  
  // Ref to store references to supplier rows for scrolling
  const supplierRowRefs = React.useRef({});

  // Single ref callback function to prevent recreation on every render
  const setSupplierRowRef = React.useCallback((el) => {
    if (el) {
      const supplierId = el.getAttribute('data-supplier-id');
      if (supplierId) {
        supplierRowRefs.current[supplierId] = el;
      }
    }
  }, []);

  const useGroupedValues =
    tableEntriesGrouped && Object.keys(tableEntriesGrouped).length > 0;

  const shouldShowData =
    sortedColumns &&
    sortedColumns.length > 0 &&
    Object.keys(tableEntries).length > 0;

  function handleSort(column) {
    let newDirection;
    if (sortColumn === column) {
      newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
    } else {
      if (column === "name") {
        newDirection = "asc";
      } else {
        newDirection = "desc";
      }
      setSortColumn(column);
      setSortDirection(newDirection);
    }

    // Notify parent component if callback provided
    if (onSortChange) {
      onSortChange(column, newDirection);
    }
  }

  // Sort suppliers by selected column and direction
  const sortedSupplierIds = useMemo(() => {
    if (useGroupedValues) {
      return getSortedSupplierIds(
        suppliers,
        tableEntriesGrouped,
        sortColumn,
        sortDirection
      );
    } else {
      return getSortedSupplierIds(
        suppliers,
        tableEntries,
        sortColumn,
        sortDirection
      );
    }
  }, [
    suppliers,
    tableEntries,
    tableEntriesGrouped,
    sortColumn,
    sortDirection,
    useGroupedValues,
  ]);

  // Calculate pagination values
  const totalPages = Math.ceil(sortedSupplierIds.length / pageSize);
  const paginatedSupplierIds = useMemo(() => {
    return sortedSupplierIds.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );
  }, [sortedSupplierIds, currentPage, pageSize]);

  function handlePageSizeChange(e) {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
  }

  function toggleSupplierSelection(supplierId) {
    setSelectedSupplierId(
      selectedSupplierId === supplierId ? null : supplierId
    );
    logEventCustom("select_content", {
      content_type: "supplier_insights_supplier",
      content_id: supplierId,
    });
  }

  function handleSupplierClick(supplierId) {
    // Find the index of the supplier in the sorted list
    const supplierIndex = sortedSupplierIds.findIndex(id => id === supplierId);
    
    if (supplierIndex !== -1) {
      // Calculate which page the supplier is on (1-indexed)
      const supplierPage = Math.ceil((supplierIndex + 1) / pageSize);
      
      // Navigate to the correct page if needed
      if (supplierPage !== currentPage) {
        setCurrentPage(supplierPage);
      }
    }
    
    setSelectedSupplierId(supplierId);
    logEventCustom("select_content", {
      content_type: "supplier_insights_supplier_chart",
      content_id: supplierId,
    });
  }

  // Handle scrolling when selectedSupplierId or currentPage changes
  React.useEffect(() => {
    if (selectedSupplierId) {
      const rowElement = supplierRowRefs.current[selectedSupplierId];
      if (rowElement) {
        // Use requestAnimationFrame to ensure DOM has updated after state change
        requestAnimationFrame(() => {
          rowElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        });
      }
    }
  }, [selectedSupplierId, currentPage]);

  function handleExportToExcel() {
    // Generate a filename with date
    const date = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
    const filename = `supplier_data_${date}.xlsx`;

    // Log export event
    logEventCustom("export_supplier_insights", {
      content_type: "supplier_insights",
      format: "excel",
    });

    // Export to Excel
    exportToExcel(
      suppliers,
      useGroupedValues ? tableEntriesGrouped : tableEntries,
      filename,
      t
    );
  }

  // Build filter status text
  function getFilterStatusText() {
    const filters = [];

    if (searchQuery) {
      filters.push(`${t("search_for")} "${searchQuery}"`);
    }

    if (activeFilters?.material && activeFilters?.material !== "all") {
      filters.push(`${t("material")} "${activeFilters?.material}"`);
    }

    if (
      activeFilters?.dateRange?.startDate &&
      activeFilters?.dateRange?.endDate &&
      activeFilters?.dateRange?.startDate !== null &&
      activeFilters?.dateRange?.endDate !== null
    ) {
      const start = new Date(
        activeFilters?.dateRange?.startDate
      ).toLocaleDateString();
      const end = new Date(
        activeFilters?.dateRange?.endDate
      ).toLocaleDateString();
      filters.push(`${t("timeframe_title")} ${start} ${t("to")} ${end}`);
    }

    if (filters.length > 0) {
      return `${t("filtered_by")}: ${filters.join(", ")}`;
    }

    return "";
  }

  function getSupplierNameCell(supplierId) {
    const supplier = suppliers.find((supplier) => supplier.id === supplierId);
    return (
      <td className="name-cell">
        {supplier.display_name || supplier.name}
        {supplier.supplier_code && (
          <div className="description">
            {t("code")}: {supplier.supplier_code}
          </div>
        )}
      </td>
    );
  }

  // Reset to first page when search query or filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeFilters]);

  // Update local sort state when props change
  React.useEffect(() => {
    setSortColumn(defaultSortColumn);
    setSortDirection(defaultSortDirection);
  }, [defaultSortColumn, defaultSortDirection]);

  // Update page size when prop changes
  React.useEffect(() => {
    setPageSize(defaultPageSize);
  }, [defaultPageSize]);

  return (
    <div className="supplier-table-container">
      {/* Search and filter results info */}
      <div className="search-results-info">
        <div className="results-count">
          {t("found_suppliers")} {suppliers.length}
        </div>
        {showSearchInfo &&
          (searchQuery ||
            activeFilters?.material !== "all" ||
            (activeFilters?.dateRange?.startDate &&
              activeFilters?.dateRange?.endDate)) && (
            <div className="filter-status">{getFilterStatusText()}</div>
          )}
        {currentUser.info.user_flags.includes('admin') && 
        <button
          onClick={handleExportToExcel}
          className="export-excel-button"
          disabled={suppliers.length === 0}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {t('export_to_excel')}
        </button>}
      </div>
      <div className="w-full overflow-x-auto">
        <table className="supplier-table">
          {/* Header of the Table*/}
          <thead>
            <tr>
              <th
                className={`sortable ${sortColumn === "name" ? "active" : ""}`}
                onClick={() => handleSort("name")}
              >
                {t("supplier_name")}
                {sortColumn === "name" && (
                  <span className={`sort-arrow ${sortDirection}`}></span>
                )}
              </th>

              {shouldShowData &&
                sortedColumns.map((item) => {
                  const columnId = item.id;
                  const columnData = tableEntries[columnId];
                  if (!columnData || item.isVisible === false) return;

                  const formatter =
                    columnData.formatterHeader ||
                    columnData.formatter ||
                    ((val) => val);
                  const totalAverage = columnData.average;
                  const filteredAverage =
                    tableEntriesGrouped?.[columnId]?.average;

                  return (
                    <th
                      className={`sortable ${
                        sortColumn === columnId ? "active" : ""
                      }`}
                      onClick={() => handleSort(columnId)}
                      key={`header-${item.id}`}
                    >
                      <Tooltip title={t(columnData?.tooltip)} arrow>
                        {t(columnData.title)}
                      </Tooltip>
                      {columnData?.betaBadge && (
                        <span className="beta-badge">beta</span>
                      )}
                      {sortColumn === columnId && (
                        <span className={`sort-arrow ${sortDirection}`}></span>
                      )}

                      {/* Show average accross all suppliers if available */}
                      {columnData?.average !== undefined && (
                        <div className="header-average">
                          {!useGroupedValues ? (
                            <>
                              {t("avg")}: {formatter(totalAverage)}
                            </>
                          ) : (
                            <>
                              {t("avg")}:{" "}
                              <Tooltip
                                title={t("material_specific_value")}
                                arrow
                              >
                                <span>{formatter(filteredAverage)}</span>
                              </Tooltip>{" "}
                              <Tooltip title={t("overall_value")} arrow>
                                <span className="material-average">
                                  ({formatter(totalAverage)})
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
            </tr>
          </thead>

          {/* Body of the Table*/}
          <tbody>
            {isLoading ? (
              <tr className="loading-row">
                <td
                  colSpan={Object.keys(tableEntries).length + 1}
                  className="loading-cell"
                >
                  <div className="loading-icon-container">
                    <LoadingIcon color="rgba(0, 0, 0, 0.3)" />
                  </div>
                </td>
              </tr>
            ) : (
              paginatedSupplierIds.map((supplierId) => {
                const isSelected = selectedSupplierId === supplierId;

                return (
                  <>
                    <tr
                      key={supplierId}
                      ref={setSupplierRowRef}
                      data-supplier-id={supplierId}
                      className={
                        isSelected ? "selected clickable" : "clickable"
                      }
                      onClick={() => toggleSupplierSelection(supplierId)}
                    >
                      {getSupplierNameCell(supplierId)}
                      {shouldShowData &&
                        sortedColumns.map((item) => {
                          const columnId = item.id;
                          const columnData = tableEntries[columnId];
                          if (!columnData || item.isVisible === false) return;

                          const totalValue =
                            columnData.supplierEntries?.[supplierId];
                          const filteredValue =
                            tableEntriesGrouped?.[columnId]?.supplierEntries?.[
                              supplierId
                            ];
                          const formatter =
                            columnData?.formatter || ((val) => val);
                          const styler =
                            columnData?.styler ||
                            ((formattedValue, value) => formattedValue);

                          if (!useGroupedValues) {
                            // Only show the total value
                            return (
                              <td key={`body-${columnId}`}>
                                {styler(formatter(totalValue), totalValue)}
                              </td>
                            );
                          } else {
                            // Show both the filtered and total value
                            return (
                              <td key={`body-${columnId}`}>
                                <Tooltip
                                  title={
                                    filteredValue === "-"
                                      ? t("insufficient_data")
                                      : t("material_specific_value")
                                  }
                                  arrow
                                >
                                  <span>
                                    {styler(
                                      formatter(filteredValue),
                                      filteredValue
                                    )}
                                  </span>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    totalValue === "-"
                                      ? t("insufficient_data")
                                      : t("overall_value")
                                  }
                                  arrow
                                >
                                  <span className="total-value">{` (${formatter(
                                    totalValue
                                  )})`}</span>
                                </Tooltip>
                              </td>
                            );
                          }
                        })}
                    </tr>
                    {isSelected && (
                      <tr key={`detail-${supplierId}`} className="details-row">
                        <td colSpan={Object.keys(tableEntries).length + 1}>
                          <SupplierOverview
                            supplier={suppliers.find(
                              (supplier) => supplier.id === supplierId
                            )}
                            supplierData={
                              supplierData?.[supplierId] ||
                              supplierDataFiltered?.[supplierId] ||
                              {}
                            }
                            supplierDataFiltered={
                              supplierDataGrouped?.[supplierId] ||
                              supplierDataFiltered?.[supplierId] ||
                              {}
                            }
                            activeFilters={activeFilters}
                            onSupplierClick={handleSupplierClick}
                            chartViewMode={chartViewMode}
                            onChartViewModeChange={setChartViewMode}
                          />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Table Footer and pagination */}
      <div className="table-footer">
        <div className="page-size-selector">
          <label>{t("items_per_page")}:</label>
          <select value={pageSize} onChange={handlePageSizeChange}>
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="pagination-arrow"
            >
              &lt;
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={currentPage === pageNum ? "active" : ""}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="pagination-arrow"
            >
              &gt;
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
